import React, { useEffect, useMemo, useState } from "react";
import {
    Modal,
    Radio,
    Space,
    Form,
    DatePicker,
    Popover,
    Button,
    Input,
    RadioChangeEvent,
    Divider,
    Typography,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
    updateCampaignStateActionResponse,
    updateCampaignConfigModalDisplay,
    updateIsCampaignStateAction,
} from "@/features/modalSlice";
import { useDispatch, useSelector } from "react-redux";
import {
    AppDispatch,
    DateActions,
    IBulkRequiredConfig,
    TCommonRowData,
    TCommonRowObj,
    TUpdateRecords,
    TBudgetUpdateModules,
    UtilsModule,
    IValueBoolean,
    IDailyBudgetModalData,
    Campaign,
    TClientTimezone,
    ICampaignsSelectionData,
    COMPONENT_CONFIG_KEYS,
} from "@/ts/common-types";
import dayjs, { Dayjs } from "dayjs";
import { useGetModulesData } from "@/hooks/AllCustomHooks";
import {
    getActiveModule,
    internationalizeNumericValue,
    isDisabledDate,
    parseInternationalizedValue,
    filterArchivedCampaigns,
} from "@/ts/utils";
import { showToast } from "@/ts/modals";
import { filter, isNull, map } from "underscore";
import { setUpdateGridData } from "@/ts/filters";
import { getEndDate } from "@/workers/grid-params.worker";
import { CAMPAIGN_STATES, DATE_BUDGET_UPDATE_CONFIG } from "@/ts/constants";
const { Text } = Typography;

interface ICampaignConfigChangeModalProps {
    show: boolean;
}

type TSelectedRecords = {
    campaignsSelectionData?: {
        value: TCommonRowData;
    };
    adgroupSelectionData?: {
        value: TCommonRowData;
    };
};

const CampaignConfigChangeModal: React.FC<ICampaignConfigChangeModalProps> = ({
    show,
}) => {
    const { activeModule } = getActiveModule();
    const { updateFunc, updateRecordId, updateRecordKey, selectedData } =
        DATE_BUDGET_UPDATE_CONFIG[activeModule as TBudgetUpdateModules];

    const selectedRecords = useSelector(
        (state: TSelectedRecords) =>
            state[selectedData as keyof TSelectedRecords].value
    );
    const componentConfig = useGetModulesData({ type: "component-config" });
    const [radioValue, setRadioValue] = useState<string>(
        DateActions.NO_END_DATE
    );
    const [duration, setDuration] = useState<Dayjs | null>(dayjs());
    const [addNumDays, setAddNumDays] = useState<number>(30);
    const [loading, setLoading] = useState<boolean>(false);
    const isCampaignStateAction = useSelector(
        (state: { isCampaignStateAction: IValueBoolean }) =>
            state.isCampaignStateAction.value
    );

    const camapaignData = useSelector(
        (state: IDailyBudgetModalData) => state.dailyBudgetModalData.value
    );

    const clientTimezone = useSelector(
        (state: TClientTimezone) => state.clientTimezone.value
    );

    const gridParams = useGetModulesData({ type: "grid-params" });
    const campaignsSelectionData = useSelector(
        (state: ICampaignsSelectionData) => state.campaignsSelectionData.value
    );

    const [campaignBudget, setCampaignBudget] = useState<string | number>(
        internationalizeNumericValue(camapaignData?.budget)
    );

    const modalTitle = isCampaignStateAction
        ? "Restart campaign"
        : "Change End Date";

    const modalInfo = isCampaignStateAction
        ? "Set the new end date and required budget for the campaign to be restarted"
        : "Change the end date for all the selected campaigns";

    const dispatch = useDispatch<AppDispatch>();

    const onRadioChange = (e: RadioChangeEvent) =>
        setRadioValue(e.target.value);

    const handleOpenChange = (open: boolean) => {
        if (open && !duration) {
            setDuration(null);
        }
    };

    const handleDateChange = (date: Dayjs | null) =>
        setDuration(date === null ? null : dayjs(date));

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        setAddNumDays(+e.target.value);

    const handleClose = () => {
        dispatch(updateIsCampaignStateAction(false));
        dispatch(updateCampaignConfigModalDisplay(false));
    };

    const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        setCampaignBudget(+e.target.value);

    const handleSubmit = async () => {
        setLoading(true);
        try {
            if (radioValue === DateActions.ADD_DAYS && !addNumDays) {
                showToast("danger", "Please enter the number of days");
            } else if (radioValue === DateActions.SELECT_DATE && !duration) {
                showToast("danger", "Please select a date");
            } else {
                const recordsToProcess = isCampaignStateAction
                    ? [camapaignData]
                    : filter(selectedRecords, (c) => {
                          const isDisabledType = componentConfig[
                              COMPONENT_CONFIG_KEYS.END_DATE
                          ].disabledTypes?.includes(c.program_type);
                          const filterArchived =
                              gridParams?.isArchivedCampaignsSelectionAllowed &&
                              c.state === CAMPAIGN_STATES.ARCHIVED;
                          return !isDisabledType && !filterArchived;
                      });

                if (!recordsToProcess.length) {
                    throw new Error(
                        `Date change not allowed for selected ${activeModule}`
                    );
                }

                const updateRecords = map(
                    recordsToProcess,
                    (record: TCommonRowObj) => {
                        const endDate = getEndDate(record, radioValue, {
                            selectedDate: duration.toString(),
                            addNumDays,
                        });

                        return {
                            [updateRecordKey]:
                                record[updateRecordId].toString(),
                            attribute: ["budget"],
                            state: !isCampaignStateAction
                                ? record.state
                                : "enabled",
                            endDate: endDate
                                ? dayjs
                                      .tz(
                                          dayjs(
                                              typeof endDate === "string"
                                                  ? dayjs(endDate, "YYYY-MM-DD")
                                                  : endDate
                                          ).utc(),
                                          clientTimezone.value
                                      )
                                      .endOf("day")
                                      .valueOf()
                                : 0,
                            ...(isCampaignStateAction && {
                                budget: parseInternationalizedValue(
                                    campaignBudget
                                ),
                            }),
                            tillBudgetEnds:
                                radioValue === DateActions.NO_END_DATE,
                            equallyDivideBudget:
                                !isNull(record.equally_divide_budget) &&
                                record.equally_divide_budget &&
                                radioValue !== DateActions.NO_END_DATE,
                        };
                    }
                );

                //grid data updation logic
                const bulkRequiredConfig: IBulkRequiredConfig = {
                    selectedRows: recordsToProcess,
                    paramToFilter: updateRecordId,
                    bulkParamsToChange: [
                        {
                            key: "till_budget_ends",
                            newValue: radioValue === DateActions.NO_END_DATE,
                            isDateCalcReq: false,
                        },
                        ...(radioValue !== DateActions.NO_END_DATE
                            ? [
                                  {
                                      key: "end_date",
                                      isDateCalcReq: true,
                                      dateUpdateType: radioValue,
                                  },
                              ]
                            : []),
                        ...(isCampaignStateAction
                            ? [
                                  {
                                      key: "state",
                                      newValue: "enabled",
                                      isDateCalcReq: false,
                                  },
                                  {
                                      key: "campaign_status",
                                      newValue: "active",
                                      isDateCalcReq: false,
                                  },
                                  {
                                      key: "budget",
                                      newValue:
                                          parseInternationalizedValue(
                                              campaignBudget
                                          ),
                                      isDateCalcReq: false,
                                  },
                                  {
                                      key: "is_archived_entity",
                                      newValue: false,
                                      isDateCalcReq: false,
                                  },
                                  ...(radioValue === DateActions.NO_END_DATE
                                      ? [
                                            {
                                                key: "end_date",
                                                newValue: null,
                                                isDateCalcReq: false,
                                            },
                                        ]
                                      : []),
                              ]
                            : []),
                    ],
                    ...(radioValue !== DateActions.NO_END_DATE && {
                        dateUpdateConfig: {
                            selectedDate: duration.toString(),
                            addNumDays,
                        },
                    }),
                };

                const updateMethod = (
                    (await import("../../ts/utils")) as UtilsModule
                )[updateFunc];

                const response = await updateMethod(
                    updateRecords as TUpdateRecords
                );
                if (!response.status) {
                    throw new Error(response.message);
                }
                showToast("success", response.message);

                isCampaignStateAction &&
                    dispatch(updateCampaignStateActionResponse(response));

                setUpdateGridData({
                    isReduxState: true,
                    bulkRequiredConfig,
                });
            }
        } catch (err: any) {
            console.error(err.message);
            showToast(
                "danger",
                `Failed to send ${activeModule} update request: ${err.message}`
            );
        } finally {
            setLoading(false);
            dispatch(updateCampaignConfigModalDisplay(false));
        }
    };

    useEffect(() => {

        if (componentConfig[COMPONENT_CONFIG_KEYS.TILL_BUDGET_ENDS].isVisible) {
            if (selectedRecords?.[0]?.end_date || camapaignData?.end_date) {
                setDuration(dayjs(selectedRecords?.[0]?.end_date || camapaignData?.end_date));
                setRadioValue(DateActions.SELECT_DATE);
            } else {
                setRadioValue(DateActions.NO_END_DATE);
            }
        } else {
            setRadioValue(DateActions.ADD_DAYS);
        }

    }, [componentConfig]);

    const modalNote = useMemo(() => {
        if (gridParams?.isArchivedCampaignsSelectionAllowed) {

            const { message, isAllSelectedArchived } = filterArchivedCampaigns(campaignsSelectionData, isCampaignStateAction);

            if (isAllSelectedArchived && !isCampaignStateAction) {
                showToast("danger", message);
                dispatch(updateCampaignConfigModalDisplay(false));
            } else {
                return message;
            }
        }
    }, [show, campaignsSelectionData]);

    const ModalHeaderElement = () => (
        <div>
            <div
                className={`d-flex align-items-center my-1 ${
                    modalNote ? "" : "pb-3"
                } fs-5`}
            >
                <div>
                    <b>{modalTitle}</b>
                </div>
                <Popover
                    className="mx-2"
                    title={modalInfo}
                    styles={{
                        body: {
                            padding: "15px",
                            width: "250px",
                        },
                    }}
                    placement="right"
                >
                    <InfoCircleOutlined
                        style={{
                            transform: "translateY(5%)",
                        }}
                    />
                </Popover>
            </div>
            {modalNote && (
                <div>
                    <Text type="danger">Note - {modalNote}</Text>
                </div>
            )}
        </div>
    );


    return (
        <Modal
            centered
            open={show}
            title={<ModalHeaderElement />}
            onOk={handleClose}
            onCancel={handleClose}
            width="28vw"
            styles={{
                body: {
                    height: isCampaignStateAction ? "22vh" : "16vh",
                    overflowY: "auto",
                },
            }}
            footer={[
                <Button
                    type="primary"
                    loading={loading}
                    key="submit"
                    onClick={handleSubmit}
                >
                    Submit
                </Button>,
                <Button key="cancel" onClick={handleClose}>
                    Cancel
                </Button>,
            ]}
        >
            <div>
                <div
                    className="pt-2"
                    style={{ display: "flex", alignItems: "center" }}
                >
                    <span
                        style={{
                            fontWeight: "bold",
                            marginBottom: 103,
                        }}
                    >
                        End Date
                    </span>
                    <Form style={{ marginLeft: 50 }}>
                        <Form.Item>
                            <div>
                                <Radio.Group
                                    onChange={onRadioChange}
                                    value={radioValue}
                                >
                                    <Space direction="vertical">
                                        {componentConfig[COMPONENT_CONFIG_KEYS.TILL_BUDGET_ENDS]
                                            .isVisible && (
                                            <Radio
                                                value={DateActions.NO_END_DATE}
                                            >
                                                {`${componentConfig[COMPONENT_CONFIG_KEYS.TILL_BUDGET_ENDS].label}`}
                                            </Radio>
                                        )}
                                        <div className="d-flex align-items-center">
                                            <Radio value={DateActions.ADD_DAYS}>
                                                Add Days
                                            </Radio>
                                            <Input
                                                disabled={
                                                    radioValue !==
                                                    DateActions.ADD_DAYS
                                                }
                                                defaultValue={addNumDays}
                                                onChange={handleInputChange}
                                                style={{
                                                    border: "1.5px solid #ccc",
                                                    width: 155,
                                                    marginLeft: 45,
                                                }}
                                            />
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <Radio
                                                value={DateActions.SELECT_DATE}
                                            >
                                                Select Date
                                            </Radio>
                                            <DatePicker
                                                style={{
                                                    border: "1.5px solid #ccc",
                                                    width: 155,
                                                    marginLeft: 34,
                                                }}
                                                name="end_date_picker"
                                                disabled={
                                                    radioValue !==
                                                    DateActions.SELECT_DATE
                                                }
                                                disabledDate={isDisabledDate}
                                                allowClear={true}
                                                value={
                                                    duration
                                                }
                                                onOpenChange={handleOpenChange}
                                                onChange={handleDateChange}
                                            />
                                        </div>
                                    </Space>
                                </Radio.Group>
                            </div>
                        </Form.Item>
                    </Form>
                </div>
                {isCampaignStateAction && (
                    <>
                        <Divider
                            style={{
                                borderColor: "#DDDDDD",
                                borderWidth: 1.8,
                                margin: 0,
                            }}
                        />
                        <div className="my-3">
                            <span
                                style={{
                                    fontWeight: "bold",
                                }}
                            >
                                Daily Budget
                            </span>
                            <Input
                                id="budget-change-input"
                                defaultValue={campaignBudget}
                                value={campaignBudget}
                                onChange={handleBudgetChange}
                                onFocus={() => {
                                    setCampaignBudget(
                                        parseInternationalizedValue(
                                            campaignBudget
                                        )
                                    );
                                }}
                                onBlur={() => {
                                    setCampaignBudget(
                                        internationalizeNumericValue(
                                            campaignBudget
                                        )
                                    );
                                }}
                                style={{
                                    border: "1.5px solid #ccc",
                                    width: 155,
                                    marginLeft: 35,
                                }}
                            />
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default CampaignConfigChangeModal;
